import { useBetsMe } from '@/api/bets';
import { GameID } from '@/enums/gameId';
import { ModalsKey } from '@/enums/modalsKey';
import { useModal } from '@/hooks/modals/useModal';
import { ErrorChip, ToolTip } from '@starsoft/common/components';
import { m } from 'framer-motion';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useMemo } from 'react';
import LastMatches from '../../LastMatches';
import LastBetChip from './Chip';
import { LastUnifiedBetsChipsProps } from './props';
import { chipVariants } from './variants';

export default function LastBetsChips({ gameId }: LastUnifiedBetsChipsProps) {
  const { isOpen } = useModal(ModalsKey.LastRounds);
  const { t } = useTranslation('common');
  const {
    data: bets,
    isPending,
    error,
    refetch,
  } = useBetsMe({
    gameId,
    type: 'infinity',
  });

  const getTooltipLabel = useCallback(() => {
    switch (gameId?.toString()) {
      case GameID.Crash?.toString():
      case GameID.Slide?.toString():
      case GameID.Frost?.toString():
      case GameID.CrashTrenball?.toString():
      case GameID.Double?.toString():
        return 'see_round_details';
      case GameID.Mines?.toString():
      case GameID.Dice?.toString():
      case GameID.Tower?.toString():
      case GameID.Coinflip?.toString():
      case GameID.Limbo?.toString():
      case GameID.Splash?.toString():
        return 'see_bet_details';
    }
  }, [gameId]);

  const isLoading: boolean = !isOpen && isPending;

  const Bets: JSX.Element[] = useMemo(
    () =>
      bets
        ?.filter((_, i) => i < 20)
        ?.map(bet => (
          <ToolTip
            key={`last-bet-chip-${bet.id}`}
            titleComponent={<div>{t(getTooltipLabel())}</div>}
          >
            <m.div
              exit="exit"
              animate="open"
              initial="closed"
              variants={chipVariants}
              layoutId={`last-bet-chip-${bet.id}`}
            >
              <LastBetChip bet={bet} gameId={gameId} />
            </m.div>
          </ToolTip>
        )),
    [bets, gameId, t, getTooltipLabel],
  );

  const Skeletons: JSX.Element[] = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, i) => (
        <LastBetChip
          key={`last-bet-chip-skeleton-${i}`}
          isLoading
          gameId={gameId}
        />
      )),
    [gameId],
  );

  if (error) {
    return <ErrorChip action={refetch} cardBg />;
  }

  return (
    <LastMatches
      gameId={gameId}
      link={`?&game-id=${gameId}`}
      modal={ModalsKey.LastBets}
    >
      {isLoading ? Skeletons : Bets}
    </LastMatches>
  );
}
